/**
 * model/quotes.ts — Derivazione pura delle quote di default (kinematic chain).
 *
 * Layer `model/` (SPEC §6): three.js consentito per la sola algebra
 * vettoriale (SSOT matematico = three.js), zero side-effect. Prende la
 * `ResolvedScene` gia' risolta e produce le quote (SPEC §6 point 4: MUTO,
 * nessuna geometria ricalcolata — solo lettura di center/quaternion/dims
 * gia' pronti) — stesso stile di `beams.ts`/`connectors.ts`.
 *
 * VINCOLO (verificato sui dati reali, es. LOCKT35100.json): `assembly.width`
 * e' il DOPPIO di `module.width` — l'assembly e' la coppia joist+header
 * affiancata, non il singolo pezzo disegnato in scena. Le quote lineari del
 * connettore usano quindi `module` (coerente al 100% col box renderizzato in
 * ConnectorMesh.tsx), non `assembly`. Quote sull'assembly (dimensione a
 * catalogo, es. "100×215") restano un'estensione futura, non in v1.*/

import * as THREE from "three";
import { QUOTE_DEFAULTS } from "../config/defaults";
import { roundTo } from "../utils/math";
import type { BeamSpec, FastenerAnchor, ResolvedScene } from "./types";
import type { AngularQuote, LinearQuote, Quote, QuotePlane } from "./quotes.types";
import type { ConnectorSide, Vec3 } from "../data/types";

// ─── Convenzione ownerId (SSOT — vedi doc-comment `QuoteBase.ownerId` in
// quotes.types.ts) ─────────────────────────────────────────────
//
// `LockScene.tsx` costruisce la mappa di visibilita' per gruppo (beam/lock)
// con GLI STESSI id: usa queste funzioni invece di ricomporre il formato a
// mano, altrimenti un cambio di convenzione qui non si propagherebbe la'.

/** Id dell'elemento trave proprietario di una quota (`"beam:joist"`).*/
export function beamOwnerId(role: "header" | "joist"): string {
  return `beam:${role}`;
}

/** Id dell'elemento connettore proprietario di una quota (`"connector:0:joist"`).*/
export function connectorOwnerId(index: number, side: ConnectorSide): string {
  return `connector:${index}:${side}`;
}

// ─── Config di derivazione ──────────────────────────────────────

export interface QuoteDerivationConfig {
  /**
   * Margine (mm) oltre l'ingombro reale del solido: la linea di quota di una
   * dimensione si offsetta di "meta' della dimensione perpendicolare + questo
   * margine", cosi' sta SEMPRE fuori dalla trave/connettore invece che dentro
   * (una distanza fissa non basta appena la sezione supera il doppio del
   * margine — bug osservato con travi/connettori piu' larghi di 80mm).*/
  offsetMm: number;
  /** Raggio (mm) di default dell'arco delle quote angolari.*/
  arcRadiusMm: number;
  /** Sotto questa soglia (gradi) una quota angolare viene saltata (~0).*/
  minAngleDeg: number;
}

const DEFAULT_CONFIG: QuoteDerivationConfig = {
  offsetMm: QUOTE_DEFAULTS.offsetMm,
  arcRadiusMm: QUOTE_DEFAULTS.arcRadiusMm,
  minAngleDeg: QUOTE_DEFAULTS.minAngleDeg,
};

// ─── Label formatting (pure) ────────────────────────────────────

/**
 * Formatta una lunghezza in mm arrotondata a 1 decimale. Con `variableSymbol`
 * antepone il simbolo di form ("b"/"h", vedi it.ts) — "b = 100 mm" invece di
 * "100 mm" — cosi' si capisce subito il legame con il campo del form.*/
export function formatLinearLabel(mm: number, variableSymbol?: string): string {
  const value = `${roundTo(mm, 1)} mm`;
  return variableSymbol ? `${variableSymbol} = ${value}` : value;
}

/** Formatta un angolo in gradi arrotondato a 1 decimale — con simbolo "α = 12.4°".*/
export function formatAngleLabel(deg: number, variableSymbol?: string): string {
  const value = `${roundTo(deg, 1)}°`;
  return variableSymbol ? `${variableSymbol} = ${value}` : value;
}

// ─── Helper vettoriali (three.js, algebra pura) ─────────────────

const UNIT_X = new THREE.Vector3(1, 0, 0);
const UNIT_Y = new THREE.Vector3(0, 1, 0);
const UNIT_Z = new THREE.Vector3(0, 0, 1);

function toVec3(v: THREE.Vector3): Vec3 {
  return [v.x, v.y, v.z];
}

/** Applica un quaternion a un asse canonico e lo restituisce come Vec3.*/
function axisWorld(quaternion: THREE.Quaternion, axis: THREE.Vector3): Vec3 {
  return toVec3(axis.clone().applyQuaternion(quaternion));
}

/** Endpoint simmetrici `center ± axis*(dim/2)` come coppia di Vec3.*/
function symmetricEndpoints(center: Vec3, axisDir: Vec3, dim: number): [Vec3, Vec3] {
  const c = new THREE.Vector3(...center);
  const a = new THREE.Vector3(...axisDir);
  const half = a.multiplyScalar(dim / 2);
  return [toVec3(c.clone().sub(half)), toVec3(c.clone().add(half))];
}

/**
 * Offset (mm) che porta la linea di quota fuori dal solido lungo l'asse
 * perpendicolare scelto: meta' dell'ingombro reale su quell'asse + il
 * margine di configurazione. Senza questo calcolo un offset fisso finisce
 * dentro la trave/il connettore appena la sezione supera 2×margine.*/
function clearanceOffsetMm(perpendicularDim: number, cfg: QuoteDerivationConfig): number {
  return perpendicularDim / 2 + cfg.offsetMm;
}

/**
 * Punto sulla fibra della trave PIU' LONTANO dal connettore (richiesta
 * utente 24-09-2026), dove disegnare le quote di sezione (height/base) —
 * lontano dall'affollamento del connettore invece che al centro geometrico.
 *
 * SHEAR JOIST (bug corretto 24-09-2026, "le quote vanno per conto loro"):
 * per la joist inclinata, `spec.quaternion` resta IDENTITA' (nessuna
 * rotazione reale — vedi nota su `angularQuote`), ma la sua GEOMETRIA e'
 * deformata da uno shear lineare in `Beam.tsx:beamBaseGeometry`:
 * `y' = y − x·tan(alpha)`, `z' = z − x·tan(beta)` (x = coordinata locale
 * lungo la fibra), e la box usa `projectedLength = l·cos(alpha)` come
 * lunghezza reale, non `dims.l`. Senza riprodurre ESATTAMENTE questa stessa
 * formula, il punto di sezione cade fuori dal solido reale invece di
 * seguirlo. La sezione stessa (height/base) resta una linea DRITTA — lo
 * shear dipende solo da x, quindi a x fissato sposta SOLO la posizione, non
 * inclina la linea (i lati verticali/orizzontali del cuneo restano dritti,
 * solo le facce superiore/inferiore sono in pendenza).
 *
 * Confronta le due estremita' per distanza reale da `referencePoint` (il
 * piano connettore) invece di assumere un segno fisso: joist e header hanno
 * formule di centratura diverse in `beams.ts` (il joist cresce interamente
 * verso l'esterno dal connettore, l'header e' centrato SUL connettore), un
 * confronto per distanza resta corretto per entrambi senza doverle conoscere.*/
function beamCrossSectionCenter(
  spec: BeamSpec,
  lengthAxis: Vec3,
  heightAxis: Vec3,
  baseAxis: Vec3,
  referencePoint: Vec3,
  pick: "far" | "near" = "far",
): Vec3 {
  const hasSkew = spec.role === "joist" && (spec.skewAlphaDeg !== 0 || spec.skewBetaDeg !== 0);
  const alphaRad = THREE.MathUtils.degToRad(spec.skewAlphaDeg);
  const betaRad = THREE.MathUtils.degToRad(spec.skewBetaDeg);
  //Stessa lunghezza REALE della box in Beam.tsx (proiettata quando inclinata).
  const halfLen = (hasSkew ? spec.dims.l * Math.cos(alphaRad) : spec.dims.l) / 2;

  const center = new THREE.Vector3(...spec.center);
  const lengthDir = new THREE.Vector3(...lengthAxis);
  const heightDir = new THREE.Vector3(...heightAxis);
  const baseDir = new THREE.Vector3(...baseAxis);

  const endAt = (x0: number): THREE.Vector3 => {
    const p = center.clone().addScaledVector(lengthDir, x0);
    if (hasSkew) {
      //Stesso shear di Beam.tsx: y' = y − x·tan(alpha), z' = z − x·tan(beta).
      p.addScaledVector(heightDir, -x0 * Math.tan(alphaRad));
      p.addScaledVector(baseDir, -x0 * Math.tan(betaRad));
    }
    return p;
  };

  const candidateA = endAt(halfLen);
  const candidateB = endAt(-halfLen);
  const ref = new THREE.Vector3(...referencePoint);
  const aIsFarther = candidateA.distanceToSquared(ref) >= candidateB.distanceToSquared(ref);
  //`near` (30-09-2026): la sezione all'incastro, per le distanze lock → trave.
  return toVec3(aIsFarther === (pick === "far") ? candidateA : candidateB);
}

// ─── Associazione quota → viste ortografiche (piani) ─────────────

/**
 * Direzione di vista di ciascun piano ortografico — stessi versori di
 * `LockScene.tsx` (`PRESET_DIRECTIONS`): la camera "front" guarda lungo Z
 * (inquadra il piano X-Y), "top" lungo Y (piano X-Z), "side" lungo X (piano
 * Y-Z). SSOT qui perche' `model/` non importa da `LockScene.tsx` (comment
 * boundary SPEC §6); se i preset cambiassero direzione andrebbero allineati
 * a mano nei due punti.*/
const VIEW_AXIS: Record<QuotePlane, THREE.Vector3> = {
  front: new THREE.Vector3(0, 0, 1),
  top: new THREE.Vector3(0, 1, 0),
  side: new THREE.Vector3(1, 0, 0),
};
const ALL_PLANES: QuotePlane[] = ["front", "top", "side"];

//Sopra questa soglia di |coseno| una direzione e' considerata "allineata"
//all'asse di vista (~11° di tolleranza da un asse puro): sufficiente per gli
//assi esattamente ortogonali di v1 (travi/connettore), permissivo per un
//futuro asse leggermente inclinato (skew) senza escluderlo per errore.
const AXIS_ALIGNMENT_THRESHOLD = 0.98;

/**
 * Viste in cui una quota LINEARE e' leggibile: tutte tranne quella la cui
 * camera guarda (quasi) parallela al segmento misurato — li' il segmento
 * appare come un punto (es. una misura lungo Z non si legge nel piano X-Y).*/
function planesForLinearAxis(axisDir: Vec3): QuotePlane[] {
  const dir = new THREE.Vector3(...axisDir).normalize();
  return ALL_PLANES.filter((plane) => Math.abs(dir.dot(VIEW_AXIS[plane])) < AXIS_ALIGNMENT_THRESHOLD);
}

/**
 * Viste in cui una quota ANGOLARE mostra l'angolo vero (non in scorcio):
 * solo quella la cui camera guarda (quasi) lungo l'asse di rotazione
 * dell'angolo — l'opposto della regola lineare.*/
function planesForRotationAxis(axisDir: Vec3): QuotePlane[] {
  const dir = new THREE.Vector3(...axisDir).normalize();
  return ALL_PLANES.filter((plane) => Math.abs(dir.dot(VIEW_AXIS[plane])) >= AXIS_ALIGNMENT_THRESHOLD);
}

// ─── Quote lineari — travi ───────────────────────────────────────

/**
 * Quote lineari nominali di una trave: height e base (sezione trasversale)
 * sugli assi locali perpendicolari alla fibra. Assi ricavati SOLO dal
 * quaternion (`spec.quaternion`), quindi il calcolo e' identico e corretto
 * per header sia "beam" sia "column" — cambia l'orientamento world del
 * quaternion (SPEC G8, `buildBeamQuaternion`), non la logica qui.
 *
 * NOTA (richiesta utente 24-09-2026): la quota `length` (`dims.l`, es. "700
 * mm") e' disabilitata di default su ENTRAMBE le travi — non serve per ora.
 * Le due quote restanti sono FUORI dal solido, ciascuna offset sull'ALTRO
 * asse di sezione (height ← base, base ← height): non si sovrappongono e non
 * dipendono da `dims.l`. Ogni offset e' "meta' della dimensione
 * perpendicolare reale + margine" (`clearanceOffsetMm`), non una distanza
 * fissa — altrimenti con sezioni piu' larghe del margine la linea di quota
 * finisce DENTRO la trave invece che fuori.
 *
 * `formField`/`variableSymbol` legano ogni quota al campo del form che la
 * genera, STESSA notazione della UI (`it.ts`: "Larghezza header (b)",
 * "Altezza header (h)"): il campo form "width" e' la variabile strutturale
 * "b" (base), il campo form "height" e' "h" — la label mostra il simbolo
 * cosi' si capisce subito il legame (es. "b = 100 mm").
 *
 * POSIZIONE (richiesta utente 24-09-2026): height/base non sono centrate sul
 * centro geometrico della trave ma sull'estremita' PIU' LONTANA dal
 * connettore, shear della joist incluso (`beamCrossSectionCenter`), per non
 * affollare la zona dell'incastro E per seguire il pezzo quando e' inclinato
 * invece di restare "per conto suo".*/
function beamQuotes(
  role: "header" | "joist",
  spec: BeamSpec,
  referencePoint: Vec3,
  cfg: QuoteDerivationConfig,
): LinearQuote[] {
  const q = spec.quaternion;
  const lengthAxis = axisWorld(q, UNIT_X.clone());
  const heightAxis = axisWorld(q, UNIT_Y.clone());
  const baseAxis = axisWorld(q, UNIT_Z.clone());

  // HEADER "COLUMN" (30-09-2026, richiesta utente): l'estremita' lontana dal
  // connettore e' il FONDO della colonna — tagliata nella perspective del
  // report e in basso nella front. La sezione va quindi in CIMA (estremita'
  // dell'incastro), con entrambe le linee sollevate sopra la colonna (+Y
  // mondo) invece che offsettate l'una sull'asse dell'altra (entrambi
  // orizzontali per una colonna).
  const isColumn = spec.role === "header" && spec.axis === "y";
  const crossSectionAt = beamCrossSectionCenter(
    spec,
    lengthAxis,
    heightAxis,
    baseAxis,
    referencePoint,
    isColumn ? "near" : "far",
  );
  // Colonna (30-09-2026, seconda correzione): le due quote al centro del top
  // si accavallavano con quelle dell'incastro nella perspective. Vanno sugli
  // SPIGOLI ESTERNI del top — h (lungo X) sullo spigolo −Z, b (lungo Z) sullo
  // spigolo −X, i lati lontani dal joist — con offset in diagonale verso
  // l'esterno e verso l'alto: fuori dal top in perspective, sopra la colonna
  // nella front (dove −Z e' l'asse di vista e resta solo la componente su).
  const SQRT1_2 = Math.SQRT1_2;
  const halfAlongWorld = (axis: 0 | 1 | 2) =>
    (Math.abs(heightAxis[axis]) * spec.dims.h) / 2 + (Math.abs(baseAxis[axis]) * spec.dims.b) / 2;
  const hAt: Vec3 = isColumn
    ? [crossSectionAt[0], crossSectionAt[1], crossSectionAt[2] - halfAlongWorld(2)]
    : crossSectionAt;
  const bAt: Vec3 = isColumn
    ? [crossSectionAt[0] - halfAlongWorld(0), crossSectionAt[1], crossSectionAt[2]]
    : crossSectionAt;
  const hOffsetDir: Vec3 = isColumn ? [0, SQRT1_2, -SQRT1_2] : baseAxis;
  const hOffsetMm = isColumn ? cfg.offsetMm : clearanceOffsetMm(spec.dims.b, cfg);
  const bOffsetDir: Vec3 = isColumn ? [-SQRT1_2, SQRT1_2, 0] : heightAxis;
  const bOffsetMm = isColumn ? cfg.offsetMm : clearanceOffsetMm(spec.dims.h, cfg);
  const [hFrom, hTo] = symmetricEndpoints(hAt, heightAxis, spec.dims.h);
  const [bFrom, bTo] = symmetricEndpoints(bAt, baseAxis, spec.dims.b);

  const formMember = role === "header" ? "headerMember" : "joistMember";
  const ownerId = beamOwnerId(role);
  return [
    {
      id: `${ownerId}:height`,
      source: "default",
      ownerId,
      kind: "linear",
      measure: "beam-height",
      from: hFrom,
      to: hTo,
      offsetDir: hOffsetDir,
      offsetMm: hOffsetMm,
      planes: planesForLinearAxis(heightAxis),
      formField: `${formMember}.height`,
      variableSymbol: "h",
      label: formatLinearLabel(spec.dims.h, "h"),
      value: spec.dims.h,
    },
    {
      id: `${ownerId}:base`,
      source: "default",
      ownerId,
      kind: "linear",
      measure: "beam-width",
      from: bFrom,
      to: bTo,
      offsetDir: bOffsetDir,
      offsetMm: bOffsetMm,
      planes: planesForLinearAxis(baseAxis),
      //Campo form "width" == variabile strutturale "b" (base) — vedi it.ts.
      formField: `${formMember}.width`,
      variableSymbol: "b",
      label: formatLinearLabel(spec.dims.b, "b"),
      value: spec.dims.b,
    },
  ];
}

// ─── Quote lineari — connettore (module, non assembly) ──────────
//
// DISABILITATA (richiesta utente 24-09-2026): "la misura sul connettore per
// il momento non serve" — commentata invece che eliminata, per riattivarla
// basta togliere il blocco di commento e la chiamata in `deriveDefaultQuotes`.
//
// /**
//  * Quote lineari del box `module` di UNA istanza connettore (di default: solo
//  * la joist del primo connettore). Assi locali coerenti con
//  * `ConnectorMesh.tsx` (`boxGeometry([thickness, height, width])` → locale
//  * X=thickness, Y=height, Z=width). Offset "meta' dimensione perpendicolare +
//  * margine" (`clearanceOffsetMm`, stesso principio delle travi): fuori dal
//  * box invece che dentro. Niente `formField`/`variableSymbol`: le dimensioni
//  * del connettore vengono dal catalogo/GLB, non da un campo del form.*/
// function connectorQuotes(instance: ConnectorInstance, cfg: QuoteDerivationConfig): LinearQuote[] {
//   const q = instance.quaternion;
//   const thicknessAxis = axisWorld(q, UNIT_X.clone());
//   const heightAxis = axisWorld(q, UNIT_Y.clone());
//   const widthAxis = axisWorld(q, UNIT_Z.clone());
//
//   const { height, width, thickness } = instance.module;
//   const [wFrom, wTo] = symmetricEndpoints(instance.position, widthAxis, width);
//   const [hFrom, hTo] = symmetricEndpoints(instance.position, heightAxis, height);
//
//   const ownerId = connectorOwnerId(instance.index, instance.side);
//   return [
//     {
//       id: `${ownerId}:width`,
//       source: "default",
//       ownerId,
//       kind: "linear",
//       measure: "connector-width",
//       from: wFrom,
//       to: wTo,
//       offsetDir: heightAxis,
//       offsetMm: clearanceOffsetMm(height, cfg),
//       planes: planesForLinearAxis(widthAxis),
//       label: formatLinearLabel(width),
//       value: width,
//     },
//     {
//       id: `${ownerId}:height`,
//       source: "default",
//       ownerId,
//       kind: "linear",
//       measure: "connector-height",
//       from: hFrom,
//       to: hTo,
//       offsetDir: thicknessAxis,
//       offsetMm: clearanceOffsetMm(thickness, cfg),
//       planes: planesForLinearAxis(heightAxis),
//       label: formatLinearLabel(height),
//       value: height,
//     },
//   ];
// }

// ─── Quote lineari — notch (taglio all'incastro, header/joist) ──

/**
 * Centro-mondo del box di taglio del notch (richiesta utente 24-09-2026):
 * "due dimensioni sul piano X-Z e l'altezza sul piano Y-Z", per il notch su
 * header E joist. Serve la posizione LOCALE del taglio, che dipende
 * dall'orientamento della trave — DUPLICATA (non importata) da
 * `components/beams/Beam.tsx:notchLocalPosition`: quella funzione e' nel
 * path di rendering condiviso viewer+PDF (`gitnexus impact` = rischio ALTO,
 * 7 simboli coinvolti), quindi si preferisce copiare la stessa formula pura
 * qui piuttosto che rifattorizzarla. TENERE SINCRONIZZATE le due copie se la
 * geometria del notch cambia in Beam.tsx.
 *
 * SEMPLIFICAZIONE (verificata a mano nei 3 casi joist-beam/header-beam/
 * header-column): il piano di taglio e' SEMPRE parallelo al piano connettore
 * fisso (`ConnectorPlane`, normale mondo +X — SPEC G1), quindi le ESTENSIONI
 * del notch (depth/height/width) risultano SEMPRE allineate agli assi mondo
 * fissi X/Y/Z una volta applicato `spec.quaternion` alla posizione locale,
 * indipendentemente dall'orientamento della trave — non serve ruotare le
 * DIREZIONI delle quote, solo il CENTRO del box.*/
function notchWorldCenter(spec: BeamSpec): Vec3 | null {
  const notch = spec.notch;
  if (!notch) return null;

  let local: Vec3;
  if (notch.target === "joist-beam") {
    const alphaRad = THREE.MathUtils.degToRad(spec.skewAlphaDeg);
    const betaRad = THREE.MathUtils.degToRad(spec.skewBetaDeg);
    const projectedLength = spec.dims.l * Math.cos(alphaRad);
    const connectorFaceOffsetY = (spec.dims.l * Math.sin(alphaRad)) / 2;
    const connectorFaceOffsetZ = (spec.dims.l * Math.cos(alphaRad) * Math.tan(betaRad)) / 2;
    local = [-projectedLength / 2 + notch.depth / 2, connectorFaceOffsetY, connectorFaceOffsetZ];
  } else if (notch.target === "header-beam" || spec.axis === "z") {
    local = [0, spec.dims.h / 2 - notch.height / 2, -spec.dims.b / 2 + notch.depth / 2];
  } else {
    local = [spec.dims.l / 2 - notch.height / 2, -spec.dims.h / 2 + notch.depth / 2, 0];
  }

  const world = new THREE.Vector3(...local)
    .applyQuaternion(spec.quaternion)
    .add(new THREE.Vector3(...spec.center));
  return toVec3(world);
}

/**
 * Quote del notch (taglio all'incastro) per UNA trave, se questa e' quella
 * tagliata (`spec.notch !== null` — al piu' una fra header/joist, secondo
 * `CutTarget`). Tre quote sugli assi mondo fissi (vedi `notchWorldCenter`):
 * `depth` e `width` sul piano X-Z (richiesta utente: "due dimensioni sul
 * piano X-Z"), `height` sul piano Y-Z ("l'altezza sul piano Y-Z"). Stesso
 * `ownerId` della trave (`beam:${role}`): nascondere la trave nasconde anche
 * il suo notch, senza wiring aggiuntivo in `LockScene.tsx`. Niente
 * `formField`/`variableSymbol`: depth/height/width derivano dal catalogo
 * connettore (`connector_assembly.thickness` ecc.), non da un campo form.
 *
 * REPORT (30-09-2026, richiesta utente):
 * - FRONT con header "beam": nessuna quota del notch (`planes` senza
 *   "front"); con header "column" restano depth e height;
 * - TOP: quote spostate FUORI da connettore e viti — depth sopra il notch
 *   (−Z mondo, il su della top), width di lato verso l'esterno della trave
 *   tagliata, oltre la vite piu' lunga di quel lato;
 * - viste laterali: mai (lo decide la capture con `includeIds`).*/
function notchQuotes(
  role: "header" | "joist",
  spec: BeamSpec,
  scene: ResolvedScene,
  cfg: QuoteDerivationConfig,
): LinearQuote[] {
  const notch = spec.notch;
  const center = notchWorldCenter(spec);
  if (!notch || !center) return [];

  const worldX: Vec3 = [1, 0, 0];
  const worldY: Vec3 = [0, 1, 0];
  const worldZ: Vec3 = [0, 0, 1];

  //Depth e width si leggono nella TOP: vanno SOPRA l'assieme, non al centro
  //del notch — li' stavano dentro la trave semitrasparente, che copriva il
  //testo (bug 30-09-2026: etichette vuote nella top del report).
  const lock = connectorAssemblyBounds(scene);
  const topY = beamBounds(scene.beams.header).union(beamBounds(scene.beams.joist)).union(lock).max.y + 1;
  const topCenter: Vec3 = [center[0], topY, center[2]];
  const [dFrom, dTo] = symmetricEndpoints(topCenter, worldX, notch.depth);
  const [wFrom, wTo] = symmetricEndpoints(topCenter, worldZ, notch.width);
  const [hFrom, hTo] = symmetricEndpoints(center, worldY, notch.height);

  //Esterno della trave tagliata lungo X: header a −X, joist a +X (beams.ts).
  const outward: Vec3 = role === "header" ? [-1, 0, 0] : [1, 0, 0];
  const longestScrewMm = Math.max(0, ...scene.fasteners.filter((f) => f.side === role).map((f) => f.lengthMm));
  const isColumn = scene.beams.header.axis === "y";
  const margin = cfg.offsetMm / 2;

  const ownerId = beamOwnerId(role);
  // Profondita' (30-09-2026, seconda correzione): due copie, una per vista.
  // TOP: oltre TUTTE le facce vicine all'incastro (connettore, joist e, se
  // colonna, header — la trave header "beam" no: e' lunga in Z e porterebbe
  // la quota fuori dall'inquadratura), cosi' non si accavalla con le distanze
  // laterali. FRONT (solo colonna): sopra la quota h della colonna.
  // Entrambe con l'etichetta OLTRE la freccia: 22 mm non contengono il testo.
  const joistSection = beamSectionAtJoint(scene.beams.joist, scene.plane.origin);
  const faceZ = [lock.min.z, joistSection.center[2] - sectionHalfAlong(scene.beams.joist, joistSection, 2)];
  if (isColumn) {
    const headerSection = beamSectionAtJoint(scene.beams.header, scene.plane.origin);
    faceZ.push(headerSection.center[2] - sectionHalfAlong(scene.beams.header, headerSection, 2));
  }
  const topClearZ = Math.min(...faceZ) - margin;
  const depthQuote = (id: string, offsetDir: Vec3, offsetMm: number, planes: QuotePlane[]): LinearQuote => ({
    id,
    source: "default",
    ownerId,
    kind: "linear",
    measure: "custom",
    from: dFrom,
    to: dTo,
    offsetDir,
    offsetMm,
    planes,
    labelPlacement: "end",
    label: formatLinearLabel(notch.depth),
    value: notch.depth,
  });
  return [
    depthQuote(`${ownerId}:notch-depth`, [0, 0, -1], Math.max(center[2] - topClearZ, 0), ["top"]),
    ...(isColumn ? [depthQuote(`${ownerId}:notch-depth-front`, [0, 1, 0], cfg.offsetMm * 1.5, ["front"])] : []),
    {
      id: `${ownerId}:notch-width`,
      source: "default",
      ownerId,
      kind: "linear",
      measure: "custom",
      from: wFrom,
      to: wTo,
      offsetDir: outward,
      offsetMm: notch.depth / 2 + longestScrewMm + margin,
      planes: ["top"],
      label: formatLinearLabel(notch.width),
      value: notch.width,
    },
    // Altezza SOLO per il notch sulla header (30-09-2026, richiesta utente):
    // sulla joist il taglio attraversa tutta la sezione (`notch.height` =
    // altezza joist, `resolveNotch` in beams.ts — prima era un cutter fisso da
    // 1000 mm e la quota usciva dall'assieme), quindi ripeterebbe la quota h
    // della joist.
    ...(role === "joist"
      ? []
      : [
          {
            id: `${ownerId}:notch-height`,
            source: "default",
            ownerId,
            kind: "linear",
            measure: "custom",
            from: hFrom,
            to: hTo,
            offsetDir: outward,
            offsetMm: notch.depth / 2 + margin,
            planes: isColumn ? ["front"] : [],
            label: formatLinearLabel(notch.height),
            value: notch.height,
          } satisfies LinearQuote,
        ]),
  ];
}

// ─── Quote lineari — distanze lock → trave (report, 30-09-2026) ──

/** Estensione world (AABB) di un box orientato: centro, quaternion, semi-dimensioni sugli assi locali X/Y/Z.*/
function orientedBoxBounds(center: Vec3, quaternion: THREE.Quaternion, half: Vec3): THREE.Box3 {
  const box = new THREE.Box3();
  const corner = new THREE.Vector3();
  for (let i = 0; i < 8; i += 1) {
    corner
      .set(i & 1 ? half[0] : -half[0], i & 2 ? half[1] : -half[1], i & 4 ? half[2] : -half[2])
      .applyQuaternion(quaternion)
      .add(new THREE.Vector3(...center));
    box.expandByPoint(corner);
  }
  return box;
}

/**
 * Ingombro world del CONNETTORE (richiesta utente 30-09-2026: "la misurazione
 * va fatta dal bordo del connettore" — "un conto e' il modulo, un altro il
 * connettore"): SOLO `connector_assembly` (es. LOCKT100265: 100 × 265 × 22),
 * mai `connector_module` (25 × 251.9 × 21.5, l'unita' ripetuta del pezzo).
 * Un box per connettore (indice), unione per le coppie:
 * - Z: centro dei suoi pezzi ± `assembly.width/2`;
 * - Y: top = top REALE del pezzo joist (`position.y + module.height/2`, quindi
 *   segue distanza dal bordo superiore e shift notch/alpha), fondo = top −
 *   `assembly.height`;
 * - X: centro dei suoi pezzi ± `assembly.thickness/2`.
 * ALLINEATO AGLI ASSI MONDO, mai ruotato col quaternion dell'istanza: il piano
 * connettore e' fisso (normale +X, SPEC G1, come in `notchWorldCenter`), e
 * ruotando lo spessore finiva su Z (bug 30-09-2026: quote top di 139.3 mm su
 * joist b = 300, cioe' (300 − `module.thickness`)/2).*/
function connectorAssemblyBounds(scene: ResolvedScene): THREE.Box3 {
  const box = new THREE.Box3();
  const indices = new Set(scene.connectors.map((c) => c.index));
  for (const index of indices) {
    const pieces = scene.connectors.filter((c) => c.index === index);
    const joist = pieces.find((c) => c.side === "joist");
    const first = pieces[0];
    if (!joist || !first) continue;
    const { width, height, thickness } = first.assembly;
    const centerX = pieces.reduce((sum, c) => sum + c.position[0], 0) / pieces.length;
    const centerZ = pieces.reduce((sum, c) => sum + c.position[2], 0) / pieces.length;
    const topY = joist.position[1] + joist.module.height / 2;
    box.union(
      new THREE.Box3(
        new THREE.Vector3(centerX - thickness / 2, topY - height, centerZ - width / 2),
        new THREE.Vector3(centerX + thickness / 2, topY, centerZ + width / 2),
      ),
    );
  }
  return box;
}

/**
 * Estremi Z (mondo) dei pezzi di UN lato del connettore, ciascuno con la PROPRIA `assembly.width`.
 * Con un lock header maggiorato l'header e' piu' largo del joist: le quote dal bordo del lock alla trave
 * lato header vanno prese da qui, non da `connectorAssemblyBounds` (che usa l'assembly del joist).
 * Senza override i due lati coincidono. `fallback` se il lato non ha pezzi.*/
function lockZRangeOfSide(
  scene: ResolvedScene,
  side: "joist" | "header",
  fallback: THREE.Box3,
): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;
  for (const piece of scene.connectors) {
    if (piece.side !== side) continue;
    min = Math.min(min, piece.position[2] - piece.assembly.width / 2);
    max = Math.max(max, piece.position[2] + piece.assembly.width / 2);
  }
  return Number.isFinite(min) ? { min, max } : { min: fallback.min.z, max: fallback.max.z };
}

/** Ingombro world di una trave (box l×h×b sul quaternion) — per mettere le quote davanti a tutto.*/
function beamBounds(spec: BeamSpec): THREE.Box3 {
  return orientedBoxBounds(spec.center, spec.quaternion, [spec.dims.l / 2, spec.dims.h / 2, spec.dims.b / 2]);
}

/**
 * Sezione della trave all'incastro: centro + assi world. Header "beam":
 * proiezione del centro connettori sulla fibra (il connettore sta a meta'
 * trave); joist: estremita' piu' VICINA al piano connettore, shear incluso
 * (`beamCrossSectionCenter`).*/
function beamSectionAtJoint(spec: BeamSpec, referencePoint: Vec3) {
  const lengthAxis = axisWorld(spec.quaternion, UNIT_X.clone());
  const heightAxis = axisWorld(spec.quaternion, UNIT_Y.clone());
  const baseAxis = axisWorld(spec.quaternion, UNIT_Z.clone());
  let center: Vec3;
  if (spec.role === "joist") {
    center = beamCrossSectionCenter(spec, lengthAxis, heightAxis, baseAxis, referencePoint, "near");
  } else {
    const c = new THREE.Vector3(...spec.center);
    const along = new THREE.Vector3(...referencePoint).sub(c).dot(new THREE.Vector3(...lengthAxis));
    center = toVec3(c.addScaledVector(new THREE.Vector3(...lengthAxis), along));
  }
  return { center, heightAxis, baseAxis };
}

/** Quota lineare "distanza" (niente simbolo di form: misura derivata, non un input).*/
function clearanceQuote(
  id: string,
  ownerId: string,
  from: Vec3,
  to: Vec3,
  offsetDir: Vec3,
  offsetMm: number,
  planes: QuotePlane[],
): LinearQuote {
  const value = new THREE.Vector3(...from).distanceTo(new THREE.Vector3(...to));
  return {
    id,
    source: "default",
    ownerId,
    kind: "linear",
    measure: "custom",
    from,
    to,
    offsetDir,
    offsetMm,
    planes,
    label: formatLinearLabel(value),
    value,
  };
}

/** Sotto questa distanza (mm) il lock e' considerato a filo: nessuna quota.*/
const MIN_CLEARANCE_MM = 0.5;

/**
 * Semi-ingombro della sezione di una trave lungo un asse MONDO (0=X, 1=Y,
 * 2=Z): somma delle proiezioni dei due assi di sezione — vale per trave,
 * colonna e joist senza sapere quale asse locale finisce dove.*/
function sectionHalfAlong(spec: BeamSpec, section: ReturnType<typeof beamSectionAtJoint>, axis: 0 | 1 | 2): number {
  return (Math.abs(section.heightAxis[axis]) * spec.dims.h) / 2 + (Math.abs(section.baseAxis[axis]) * spec.dims.b) / 2;
}

/**
 * Distanze CONNETTORE → trave richieste per il report (30-09-2026), misurate
 * dal bordo del connettore (`connectorAssemblyBounds`, solo `connector_assembly`).
 * Una quota per vista, `planes` = la sola vista dove si legge:
 * - FRONT (X-Y): fondo connettore → fondo trave, header (a sinistra, solo
 *   "beam": il fondo di una colonna non e' vicino all'incastro) e joist (a destra);
 * - TOP (X-Z): bordi laterali connettore → facce joist (a destra) e, con
 *   header "column", → facce della colonna (a sinistra);
 * - SIDE sinistra (lato joist, camera su −X): bordi laterali → facce joist
 *   (sopra) e fondo connettore → fondo joist (di lato);
 * - SIDE destra (lato header, camera su +X): header "beam" fondo connettore →
 *   fondo trave; header "column" bordi laterali → facce della colonna (sopra).
 * Le due viste laterali condividono il piano "side": le separa l'`ownerId`
 * (la capture nasconde le quote del lato non disegnato). Le linee stanno
 * DAVANTI alle travi nella propria vista (travi semitrasparenti ma con depth
 * test: una quota dentro il volume resterebbe velata).*/
function jointClearanceQuotes(scene: ResolvedScene, cfg: QuoteDerivationConfig): LinearQuote[] {
  const quotes: LinearQuote[] = [];
  const lock = connectorAssemblyBounds(scene);
  if (lock.isEmpty()) return quotes;

  const header = scene.beams.header;
  const joist = scene.beams.joist;
  const isColumn = header.axis === "y";
  const all = beamBounds(header).union(beamBounds(joist)).union(lock);
  // Larghezza del lock lato header (maggiorata con il lock header): le quote header partono dal SUO bordo.
  const headerLockZ = lockZRangeOfSide(scene, "header", lock);
  const frontZ = all.max.z + 1;
  const topY = all.max.y + 1;
  const offsetMm = cfg.offsetMm / 2;
  const headerSection = beamSectionAtJoint(header, scene.plane.origin);
  const joistSection = beamSectionAtJoint(joist, scene.plane.origin);

  // Bordi REALI del singolo pezzo connettore (01-10-2026, correzione richiesta
  // utente: "la misura di 100 che hai plotato era quella rispetto alla
  // dimensione dell'assieme" — `lock` (`connectorAssemblyBounds`) e'
  // l'inviluppo dell'ASSEMBLY (joist+header, usa `assembly.height`), diverso
  // dal bordo del singolo pezzo quando c'e' stagger (il pezzo joist usa
  // `module.height` < `assembly.height`). Letti DIRETTAMENTE da
  // `instance.position` (centro mondo gia' risolto) ± `module.height/2`: a
  // differenza di `connectorAssemblyBounds.topY` (`position − offsetY`, che
  // torna sempre il piano "h" NON scalato), questi riflettono il vero
  // spostamento quando `joistNotchDownwardShiftMm` e' attivo (resolveScene.ts).
  const joistInstance = scene.connectors.find((c) => c.side === "joist");
  const headerInstance = scene.connectors.find((c) => c.side === "header");
  const joistOwnBottomY = joistInstance ? joistInstance.position[1] - joistInstance.module.height / 2 : lock.min.y;
  const joistOwnTopY = joistInstance ? joistInstance.position[1] + joistInstance.module.height / 2 : lock.max.y;
  const headerOwnTopY = headerInstance ? headerInstance.position[1] + headerInstance.module.height / 2 : lock.max.y;
  const headerOwnBottomY = headerInstance ? headerInstance.position[1] - headerInstance.module.height / 2 : lock.min.y;

  const push = (id: string, spec: BeamSpec, from: Vec3, to: Vec3, offsetDir: Vec3, planes: QuotePlane[]) => {
    if (new THREE.Vector3(...from).distanceTo(new THREE.Vector3(...to)) < MIN_CLEARANCE_MM) return;
    quotes.push(clearanceQuote(id, beamOwnerId(spec.role), from, to, offsetDir, offsetMm, planes));
  };

  const headerBottomY = headerSection.center[1] - sectionHalfAlong(header, headerSection, 1);
  const headerTopY = headerSection.center[1] + sectionHalfAlong(header, headerSection, 1);
  const joistBottomY = joistSection.center[1] - sectionHalfAlong(joist, joistSection, 1);
  const joistTopY = joistSection.center[1] + sectionHalfAlong(joist, joistSection, 1);
  const joistHalfZ = sectionHalfAlong(joist, joistSection, 2);
  const joistMinZ = joistSection.center[2] - joistHalfZ;
  const joistMaxZ = joistSection.center[2] + joistHalfZ;
  const headerHalfZ = sectionHalfAlong(header, headerSection, 2);
  const headerMinZ = headerSection.center[2] - headerHalfZ;
  const headerMaxZ = headerSection.center[2] + headerHalfZ;

  // FRONT
  if (!isColumn) {
    push("clearance:header:bottom", header, [lock.min.x, lock.min.y, frontZ], [lock.min.x, headerBottomY, frontZ], [-1, 0, 0], ["front"]);
  }
  push("clearance:joist:bottom", joist, [lock.max.x, lock.min.y, frontZ], [lock.max.x, joistBottomY, frontZ], [1, 0, 0], ["front"]);
  // Distanza lock-joist → filo header (01-10-2026, richiesta utente: "la
  // distanza del lock al filo del header"): SOLO quando c'e' davvero uno
  // scarto (notch+angolo attivi, `joistNotchDownwardShiftMm` in
  // `resolveScene.ts`) — misurata dalla geometria GIA' risolta (top reale
  // del connettore vs top reale della header), non ricalcolando la formula
  // qui: a scarto zero `push` la scarta da sola (MIN_CLEARANCE_MM).
  // SOLO non-column (01-10-2026, richiesta utente: "quando orientato come
  // colonna abbiamo una quota nel front view che non ci serve"): `headerTopY`
  // viene da `sectionHalfAlong` (height/base locali proiettati sull'asse
  // mondo Y), formula valida per una trave orizzontale ma NON per una
  // colonna — li' e' la fibra (asse locale X) a essere verticale, quindi
  // `headerTopY` non rappresenta il vero top della colonna e la quota mostra
  // un valore senza senso (vedi "67.5mm" nello screenshot).
  if (!isColumn) {
    push("clearance:joist:notch-shift-top", joist, [lock.max.x, joistOwnTopY, frontZ], [lock.max.x, headerTopY, frontZ], [1, 0, 0], ["front"]);
  } else {
    // Connector top → joist top = the form's connector top distance (plus any
    // notch shift); with a beam header the quote above already measures it.
    push("clearance:joist:top-distance", joist, [lock.max.x, joistOwnTopY, frontZ], [lock.max.x, joistTopY, frontZ], [1, 0, 0], ["front"]);
    // Column top → joist top: the form's "joist top offset from header". Its
    // end point is the same joist top as the quote above, so it sits one
    // step further out to keep the two from overlapping. Drops out at 0 (flush).
    const columnTopY = beamBounds(header).max.y;
    const topOffsetFrom: Vec3 = [lock.max.x, columnTopY, frontZ];
    const topOffsetTo: Vec3 = [lock.max.x, joistTopY, frontZ];
    if (new THREE.Vector3(...topOffsetFrom).distanceTo(new THREE.Vector3(...topOffsetTo)) >= MIN_CLEARANCE_MM) {
      quotes.push(clearanceQuote("clearance:joist:top-offset", beamOwnerId("joist"), topOffsetFrom, topOffsetTo, [1, 0, 0], cfg.offsetMm * 1.5, ["front"]));
    }
  }

  // TOP
  push("clearance:joist:side-min", joist, [lock.max.x, topY, joistMinZ], [lock.max.x, topY, lock.min.z], [1, 0, 0], ["top"]);
  push("clearance:joist:side-max", joist, [lock.max.x, topY, lock.max.z], [lock.max.x, topY, joistMaxZ], [1, 0, 0], ["top"]);
  if (isColumn) {
    push("clearance:header:side-min", header, [lock.min.x, topY, headerMinZ], [lock.min.x, topY, headerLockZ.min], [-1, 0, 0], ["top"]);
    push("clearance:header:side-max", header, [lock.min.x, topY, headerLockZ.max], [lock.min.x, topY, headerMaxZ], [-1, 0, 0], ["top"]);
  }

  // SIDE sinistra — lato joist (camera su −X: +Z mondo = destra schermo).
  const joistViewX = Math.min(lock.min.x, joistSection.center[0]) - 1;
  const joistLatY = Math.max(lock.max.y, joistTopY);
  push("clearance:joist:lat-min", joist, [joistViewX, joistLatY, joistMinZ], [joistViewX, joistLatY, lock.min.z], [0, 1, 0], ["side"]);
  push("clearance:joist:lat-max", joist, [joistViewX, joistLatY, lock.max.z], [joistViewX, joistLatY, joistMaxZ], [0, 1, 0], ["side"]);
  // Bordo del PEZZO joist (`joistOwnBottomY`, 01-10-2026), non dell'assembly:
  // vedi commento su `joistOwnBottomY` sopra.
  //
  // Posizione DENTRO il notch quando c'e' (01-10-2026, richiesta utente:
  // "quando esiste un notch...sarebbe meglio posizionare la quota all'interno
  // del notch"): fuori dal notch la linea di quota finiva sovrapposta al
  // bordo del legno (vedi screenshot, "70.1mm" a cavallo dello spigolo) —
  // dentro la cavita' del taglio c'e' spazio vuoto gia' pronto. Riusa
  // `notchWorldCenter` (gia' usato da `notchQuotes` per lo stesso box di
  // taglio): X/Z del centro del notch, Y invariato (e' la misura vera).
  const joistNotchCenter = notchWorldCenter(joist);
  const joistBottomQuoteX = joistNotchCenter ? joistNotchCenter[0] : joistViewX;
  const joistBottomQuoteZ = joistNotchCenter ? joistNotchCenter[2] : lock.max.z;
  push(
    "clearance:joist:side-bottom",
    joist,
    [joistBottomQuoteX, joistOwnBottomY, joistBottomQuoteZ],
    [joistBottomQuoteX, joistBottomY, joistBottomQuoteZ],
    [0, 0, 1],
    ["side"],
  );

  // SIDE destra — lato header (camera su +X: −Z mondo = destra schermo).
  const headerViewX = Math.max(lock.max.x, headerSection.center[0]) + 1;
  if (isColumn) {
    const columnTopY = Math.max(lock.max.y, headerSection.center[1]);
    push("clearance:header:lat-min", header, [headerViewX, columnTopY, headerMinZ], [headerViewX, columnTopY, headerLockZ.min], [0, 1, 0], ["side"]);
    push("clearance:header:lat-max", header, [headerViewX, columnTopY, headerLockZ.max], [headerViewX, columnTopY, headerMaxZ], [0, 1, 0], ["side"]);
    // Bordo superiore lock → bordo superiore colonna (01-10-2026, richiesta
    // utente: "quando orientazione e' column manca la distanza che abbiamo
    // calcolato in modalita' trave top to top") — stessa quota di sotto
    // (ramo "beam"), qui mancava del tutto. `beamBounds` (box orientato,
    // indipendente dal ruolo) invece di `headerTopY`/`sectionHalfAlong`: per
    // una colonna la fibra (dims.l) e' lungo l'asse mondo Y, non quello che
    // "sectionHalfAlong" assume (height/base locali proiettati su Y, quasi
    // nulli per una colonna — darebbe un top finto vicino al centro).
    const columnOwnTopY = beamBounds(header).max.y;
    push("clearance:header:side-top", header, [headerViewX, headerOwnTopY, headerLockZ.min], [headerViewX, columnOwnTopY, headerLockZ.min], [0, 0, -1], ["side"]);
  } else {
    // Bordo SUPERIORE lock → bordo superiore header (01-10-2026, correzione
    // richiesta utente: "nel header side bisogna calcolare la quota dal bordo
    // superiore del lock al bordo superiore della trave o colonna" — prima
    // era fondo→fondo, come sul lato joist; il lato header va letto al
    // contrario, dall'alto).
    push("clearance:header:side-top", header, [headerViewX, headerOwnTopY, headerLockZ.min], [headerViewX, headerTopY, headerLockZ.min], [0, 0, -1], ["side"]);
    // Bordo INFERIORE lock → bordo inferiore header (01-10-2026, ripristinata:
    // richiesta utente "prima si vedeva la distanza dal basso ora e' sparita"
    // — la quota top-to-top sopra si AGGIUNGE a questa, non la sostituisce;
    // bordo del pezzo header reale (`headerOwnBottomY`), non dell'assembly,
    // stesso principio di `joistOwnBottomY`.
    push("clearance:header:side-bottom", header, [headerViewX, headerOwnBottomY, headerLockZ.min], [headerViewX, headerBottomY, headerLockZ.min], [0, 0, -1], ["side"]);
  }
  return quotes;
}

/**
 * Copie "per vista" di misure che esistono gia' in un'altra quota (30-09-2026):
 * la stessa distanza e' ripetuta con un'altra posizione/offset perche' si
 * legga in una specifica vista ortografica del report. In una vista 3D libera
 * — viewer interattivo e perspective del report — si vedrebbero tutte, doppie
 * ("le quote si ripetono"): li' si escludono. Restano le distanze laterali
 * della top (sopra l'assieme) e quelle di fondo delle viste laterali (vicine
 * al connettore); via le copie front (all'estremita' della header, lontano
 * dall'incastro), quelle laterali "in alto" e la profondita' notch della front.
 *
 * NOTCH (01-10-2026, richiesta utente: "nella modalita' perspective ho delle
 * quote invece che voglio eliminare perche' non capibili" — screenshot con
 * profondita'/larghezza/altezza del notch tutte sovrapposte): queste quote
 * hanno `planes` ristretto a "top" (o "front" per le varianti "-front" gia'
 * escluse sopra), ma la vista "perspective" NON filtra per `planes` — solo
 * per questa lista — quindi comparivano comunque li', affollate e illeggibili
 * in una vista libera invece che nella top dove sono state pensate.
 *
 * SIDE-MIN/SIDE-MAX (01-10-2026, "persiste errore...in modalita' perspective
 * le quote si visualizzano male" — stesso bug, id diversi che mi erano
 * sfuggiti nel giro precedente): `clearance:joist/header:side-min/side-max`
 * (TOP, i 4 tick 125/125/75/75mm nello screenshot) sono quote DIVERSE da
 * `lat-min/lat-max` (SIDE, gia' escluse sopra) — stessa causa, stesso fix.
 *
 * Per coerenza con le altre quote connettore→trave di `jointClearanceQuotes`
 * (gia' tutte escluse sopra, stesso motivo): anche le varianti "side-bottom"/
 * "side-top" (SIDE, usate da `SIDE_CLEARANCE_QUOTE_IDS` per le viste laterali
 * del PDF) e "notch-shift-top" (FRONT) restano escluse dalla perspective.*/
export const PER_VIEW_COPY_QUOTE_IDS: readonly string[] = [
  "clearance:header:bottom",
  "clearance:joist:bottom",
  "clearance:joist:notch-shift-top",
  "clearance:joist:top-distance",
  "clearance:joist:top-offset",
  "clearance:joist:lat-min",
  "clearance:joist:lat-max",
  "clearance:joist:side-bottom",
  "clearance:header:lat-min",
  "clearance:header:lat-max",
  "clearance:header:side-top",
  "clearance:header:side-bottom",
  "clearance:joist:side-min",
  "clearance:joist:side-max",
  "clearance:header:side-min",
  "clearance:header:side-max",
  "beam:header:notch-depth-front",
  "beam:joist:notch-depth-front",
  "beam:header:notch-depth",
  "beam:header:notch-width",
  "beam:header:notch-height",
  "beam:joist:notch-depth",
  "beam:joist:notch-width",
];

/** Id delle quote connettore → trave delle viste laterali (la capture stampa solo queste li'). */
export const SIDE_CLEARANCE_QUOTE_IDS: readonly string[] = [
  "clearance:joist:lat-min",
  "clearance:joist:lat-max",
  "clearance:joist:side-bottom",
  "clearance:header:lat-min",
  "clearance:header:lat-max",
  "clearance:header:side-top",
  "clearance:header:side-bottom",
];

// ─── Quote angolari — alphaDeg / betaDeg ────────────────────────

/** Simbolo di form (`it.ts`: "Angolo joist α"/"Angolo joist β") e campo react-hook-form per ciascun angolo.*/
const ANGLE_META: Record<"alpha" | "beta", { symbol: string; formField: string }> = {
  alpha: { symbol: "α", formField: "joistMember.alphaDeg" },
  beta: { symbol: "β", formField: "joistMember.betaDeg" },
};

/**
 * Quota angolare in un piano (X-Y per alpha, X-Z per beta): vertice al piano
 * connettore (SPEC G1), riferimento orizzontale (1,0,0), seconda direzione
 * ruotata di `angleDeg` attorno all'asse del piano. Saltata sotto
 * `cfg.minAngleDeg` (stesso gate di `ConstraintDebug`: `alphaDeg !== 0`).
 * `planes`: solo la vista che guarda lungo `rotationAxis` mostra l'angolo
 * vero (non in scorcio). `angleDeg`/`value`/label sono lo stesso valore del
 * campo form (`ANGLE_META`) — l'eventuale segno per orientare l'arco nel
 * verso giusto va nel SEGNO di `rotationAxis`, mai in quello di `angleDeg`,
 * altrimenti la quota mostrerebbe un numero diverso da quello nel form.
 *
 * SEGNO DI `rotationAxis` (bug corretto 24-09-2026, verificato contro
 * `beams.ts:beamCenterFor` — l'UNICA fonte reale della direzione della
 * fibra, non una rotazione di pivot: il commento di `resolveScene.ts` STEP 4
 * dice esplicitamente "no rotation is applied", alpha/beta spostano solo il
 * centro trave): per `alpha`, `centerY = ... − l·sin(alphaRad)/2` → verso
 * −Y a alpha positivo; per `beta`, `centerZ = ... − l·cos(alphaRad)·tan(betaRad)/2`
 * → verso −Z a beta positivo. Il chiamante passa gli assi che riproducono
 * ESATTAMENTE questi segni (asse (0,0,−1) per alpha, (0,1,0) per beta — vedi
 * `deriveDefaultQuotes`), non dedotti qui.*/
/**
 * Fattore di raggio per alpha/beta (01-10-2026, richiesta utente: "nel top
 * view... si sovrappone l'angolo alla larghezza"): nella TOP view l'arco/
 * etichetta di beta, al raggio di default, cadeva nella stessa zona delle
 * quote laterali di `jointClearanceQuotes` (65/65/70mm) — entrambe vicine al
 * piano connettore. SOLO alpha/beta usano questo raggio maggiorato, non la
 * quota della vite inclinata (`inclinedFastenerAngleQuote`, non segnalata
 * come sovrapposta): cosi' arco+etichetta finiscono oltre l'area affollata
 * dell'incastro (`AngularDimension.tsx`: `labelPos = vertex + midDir*(radius+offset)`).*/
const JOINT_ANGLE_RADIUS_FACTOR = 2;

function angularQuote(
  measure: "alpha" | "beta",
  vertex: Vec3,
  rotationAxis: Vec3,
  angleDeg: number,
  cfg: QuoteDerivationConfig,
): AngularQuote | null {
  if (Math.abs(angleDeg) < cfg.minAngleDeg) return null;

  const dirA: Vec3 = [1, 0, 0];
  const rotQ = new THREE.Quaternion().setFromAxisAngle(
    new THREE.Vector3(...rotationAxis).normalize(),
    THREE.MathUtils.degToRad(angleDeg),
  );
  const dirB = toVec3(new THREE.Vector3(...dirA).applyQuaternion(rotQ));
  const meta = ANGLE_META[measure];

  return {
    id: `angle:${measure}`,
    source: "default",
    kind: "angular",
    measure,
    vertex,
    dirA,
    dirB,
    angleDeg,
    radiusMm: cfg.arcRadiusMm * JOINT_ANGLE_RADIUS_FACTOR,
    planes: planesForRotationAxis(rotationAxis),
    formField: meta.formField,
    variableSymbol: meta.symbol,
    label: formatAngleLabel(angleDeg, meta.symbol),
    value: angleDeg,
  };
}

// ─── Quota angolare — vite inclinata (front view, 01-10-2026) ───

/**
 * Riferimento orizzontale per l'angolo della vite inclinata: `-X` mondo da'
 * esattamente 45° per i fori inclinati del joist (`anchor.axis`), verificato
 * parsando la catena di trasformazione reale (Ry∓90°+Rx180° applicata al
 * normal locale [0,0.7071,0.7071] di questi fori — sempre nel piano X-Y,
 * mai component_rotation_deg per le inclinate). Non e' un valore sintetico:
 * l'angolo mostrato e' calcolato da `anchor.axis` reale, questo vettore serve
 * solo come lato "zero" da cui partire.*/
const INCLINED_FASTENER_REFERENCE_DIR: Vec3 = [-1, 0, 0];

/**
 * Quota angolare dell'inserimento della vite inclinata (01-10-2026, richiesta
 * utente: "aggiungere la quota di 45 gradi alla vite inclinata"), SOLO in
 * front view — non un valore fisso: calcolata dall'asse REALE della prima
 * vite inclinata in scena (`FastenerAnchor.axis`, gia' risolto con
 * `localToWorld`), cosi' riflette l'assieme davvero renderizzato invece di
 * assumere 45° (es. se in futuro l'inclinazione cambiasse per un asset).
 * Una sola quota rappresentativa: tutte le inclinate dello stesso connettore
 * condividono lo stesso angolo (stesso hole design), ripeterla per ciascuna
 * affollerebbe la vista senza aggiungere informazione.
 * `measure: "custom"` e nessun `formField`/`variableSymbol` (non e' un
 * valore del form, e' una proprieta' geometrica fissa del foro).
 * @returns null se non c'e' nessuna vite inclinata in scena.*/
function inclinedFastenerAngleQuote(scene: ResolvedScene, cfg: QuoteDerivationConfig): AngularQuote | null {
  const inclined = scene.fasteners.find((f: FastenerAnchor) => f.holeType === "inclined");
  if (!inclined) return null;

  const dirA = new THREE.Vector3(...INCLINED_FASTENER_REFERENCE_DIR).normalize();
  const dirB = new THREE.Vector3(...inclined.axis).normalize();
  const rotationAxis = new THREE.Vector3().crossVectors(dirA, dirB);
  if (rotationAxis.lengthSq() < 1e-12) return null; //direzioni degeneri/parallele

  const angleDeg = THREE.MathUtils.radToDeg(Math.acos(THREE.MathUtils.clamp(dirA.dot(dirB), -1, 1)));
  if (angleDeg < cfg.minAngleDeg) return null;

  return {
    id: "angle:inclined",
    source: "default",
    kind: "angular",
    measure: "custom",
    vertex: inclined.position,
    dirA: INCLINED_FASTENER_REFERENCE_DIR,
    dirB: toVec3(dirB),
    angleDeg,
    radiusMm: cfg.arcRadiusMm,
    planes: planesForRotationAxis(toVec3(rotationAxis.normalize())),
    label: formatAngleLabel(angleDeg),
    value: angleDeg,
  };
}

// ─── API pubblica ────────────────────────────────────────────────

/**
 * Deriva le quote di default dalla scena risolta (SPEC §8 estesa): lineari
 * per travi header (sia "beam" sia "column") e joist (height, base) e per il
 * loro notch (depth/width sul piano X-Z, height sul piano Y-Z — al piu' una
 * trave e' tagliata, secondo `CutTarget`), angolari per alphaDeg/betaDeg. Le
 * quote utente future si appendono alla stessa lista (`source: "user"`),
 * zero rendering nuovo.
 *
 * NOTA (richiesta utente 24-09-2026): le quote del box `module` del
 * connettore (`connectorQuotes`) NON sono incluse di default — non servono
 * per ora, funzione mantenuta per una riattivazione futura.
 *
 * @param scene Scena risolta (SSOT geometria, mm, Y-up).
 * @param cfg Override parziale della configurazione di derivazione.
 * @returns Lista di quote (puo' essere vuota, es. angoli a 0).*/
export function deriveDefaultQuotes(scene: ResolvedScene, cfg?: Partial<QuoteDerivationConfig>): Quote[] {
  const config: QuoteDerivationConfig = { ...DEFAULT_CONFIG, ...cfg };
  const quotes: Quote[] = [];

  quotes.push(...beamQuotes("header", scene.beams.header, scene.plane.origin, config));
  quotes.push(...beamQuotes("joist", scene.beams.joist, scene.plane.origin, config));

  quotes.push(...notchQuotes("header", scene.beams.header, scene, config));
  quotes.push(...notchQuotes("joist", scene.beams.joist, scene, config));

  quotes.push(...jointClearanceQuotes(scene, config));

  //Assi verificati contro beams.ts:beamCenterFor (bug corretto 24-09-2026 —
  //vedi commento su angularQuote): (0,0,−1) per alpha e (0,1,0) per beta
  //riproducono ESATTAMENTE i segni −sin(alpha) su Y e −tan(beta) su Z della
  //formula reale di spostamento del centro trave (non una rotazione di
  //pivot, che non esiste — resolveScene.ts STEP 4 non ruota nulla).
  const alpha = angularQuote("alpha", scene.plane.origin, [0, 0, -1], scene.alphaDeg, config);
  if (alpha) quotes.push(alpha);

  const beta = angularQuote("beta", scene.plane.origin, [0, 1, 0], scene.betaDeg, config);
  if (beta) quotes.push(beta);

  const inclinedAngle = inclinedFastenerAngleQuote(scene, config);
  if (inclinedAngle) quotes.push(inclinedAngle);

  return quotes;
}
